import React, { useEffect, useState } from "react";
import { Spin } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import { useGetConceptsFiltersMutation } from "../../../store/api/lumi/concepts";
import { Brush, CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { getConceptFilter } from "../../../utils/getConceptFilter";
import { debounce } from "lodash";
import { formatDateToUtc } from "../../../utils/formatDate";
import { useSelector } from "react-redux";
import { getLineColor } from "../../../utils/getLineColor";

export const VolumeOverTime = (props) => {
    const [chartData, setChartData] = useState([]);
    const [loading, setLoading] = useState(false);
    const { smoothLine } = props;
    const [getVolume] = useGetConceptsFiltersMutation();
    const [conceptColors, setConceptColors] = useState({});

    const labelFontSize = useSelector((state) => {
        const fontSizes = state.fontManager.fontSizes;
        const normalizedKey = Object.keys(fontSizes).find(
            (key) => key.trim() === "labelFontSize",
        );
        return fontSizes[normalizedKey] || 16;
    });

    async function getVolumeData() {
        setLoading(true);

        const conceptFilter = getConceptFilter(props);

        const { data: dataConcepts } = await getVolume({
            id: props.id,
            concept_selector: conceptFilter,
            filter: props.filters,
            breakdowns: [
                { name: props.selectedDateName, interval: props.interval },
            ],
        });
        props.setFilterDocCount(dataConcepts.filter_count);
        if (props && props.conceptList) {
            props.setLocalConceptOptions(dataConcepts.match_counts);
        }

        const breakdownData = dataConcepts.breakdowns[0].buckets;
        const conceptsMap = {};
        const conceptColors = {};

        breakdownData.forEach(({ label, match_counts, filter_count }) => {
            if (!filter_count) return;
            const formattedDate = formatDateToUtc(label, props.interval);

            match_counts.forEach(({ name, match_count, exact_match_count, color }) => {
                if (!conceptsMap[name]) {
                    conceptsMap[name] = [];
                    if (color) conceptColors[name] = color;
                }

                let count = match_count;
                switch (props.seletctedType) {
                    case "match_count":
                        count = match_count;
                        break;
                    case "exact_match_count":
                        count = exact_match_count;
                        break;
                    case "conceptual_match_count":
                        count = match_count - exact_match_count;
                        break;
                    default:
                        count = match_count; // Default to match_count
                        break;
                }

                conceptsMap[name].push({
                    date: formattedDate,
                    [name]: count,
                });
            });
        });


        const aggregatedData = Object.values(conceptsMap)
            .flat()
            .reduce((acc, curr) => {
                const existingEntry = acc.find((entry) => entry.date === curr.date);
                if (existingEntry) {
                    return acc.map((entry) =>
                        entry.date === curr.date ? { ...entry, ...curr } : entry,
                    );
                }
                return [...acc, curr];
            }, []);


        setChartData(aggregatedData);
        setConceptColors(conceptColors);
        setLoading(false);

    }

    useEffect(() => {
        setLoading(true);
        const debouncedGetVolume = debounce(getVolumeData, 1000);
        debouncedGetVolume();

        return () => {
            debouncedGetVolume.cancel();
        };
    }, [
        props.selectedPageSize,
        props.filters,
        props.conceptTypeDay,
        props.selectedDateName,
        props.interval,
        props.selectedLocalConcepts.length,
        props.conceptList,
        props.seletctedType,
    ]);

    if (loading) {
        return (
            <Spin
                className={"spinner-by-center"}
                indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
            />
        );
    }

    let labelText;
    switch (props.seletctedType) {
        case "match_count":
            labelText = "Total Matches";
            break;
        case "exact_match_count":
            labelText = "Exact Matches";
            break;
        case "conceptual_match_count":
            labelText = "Conceptual Matches";
            break;
        default:
            labelText = "Total Matches";
            break;
    }

    return (
        <div className="volume-compare" style={{ height: props.widget.layout.h * 39 - 150 }} onMouseDown={(e) => {
            if (e.target.closest(".recharts-brush")) {
                e.stopPropagation();
                e.preventDefault()
            }
        }}>
            <ResponsiveContainer width="100%" height="100%">
                <LineChart
                    data={chartData}
                    margin={{ top: 5, right: 20, left: 40, bottom: 5 }}
                >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" tick={{ fontSize: 16, color: "#000", fontWeight: "bold" }} />
                    <YAxis
                        label={{
                            value: labelText,
                            angle: -90,
                            dx: -20,
                            position: "insideLeft",
                            offset: 0,
                            style: { textAnchor: "middle", fontSize: 16, color: "#000", fontWeight: "bold" },
                        }}
                        tickFormatter={(tick) =>
                            new Intl.NumberFormat("en", {
                                notation: "compact",
                            }).format(tick)
                        }
                        tick={{ fontSize: 16, color: "#000", fontWeight: "bold" }}
                        domain={[0, "dataMax + 20"]}
                    />
                    <Tooltip wrapperStyle={{ fontSize: 15, color: "#000", fontWeight: "bold" }} />
                    <Legend
                        wrapperStyle={{
                            fontSize: `${labelFontSize}px`,
                            fontWeight: "bold",
                            color: "#000",
                        }}
                    />
                    <Brush
                        dataKey="date"
                        height={30}
                        stroke="#696969"
                        travellerWidth={12}
                        gap={2}
                        startIndex={0}
                        endIndex={chartData.length - 1}
                        fill="#D3D3D3"
                        travellerStyle={{
                            stroke: "#696969",
                            fill: "#A9A9A9",
                            cursor: "pointer",
                        }}
                        selectedBoxStyle={{
                            stroke: "#A9A9A9",
                            fill: "rgba(169, 169, 169, 0.2)",
                            cursor: "move",
                        }}
                        padding={{ top: 2, bottom: 2 }}
                    />

                    {
                        Object.keys(chartData[0] ?? {})
                            .filter((key) => key !== "date")
                            .map((key, idx) => (
                                <Line
                                    type={smoothLine ? "monotone" : "linear"}
                                    dot={!smoothLine}
                                    dataKey={key}
                                    stroke={conceptColors[key] || getLineColor(idx)}
                                    key={key}
                                />
                            ))
                    }

                </LineChart>
            </ResponsiveContainer>
        </div>
    );
};
